import { UploadCard } from '@/components/upload-card/UploadCard'
import { UploadCardStatus } from '@/components/upload-card/constants'
import { MedSummaryFileStatus } from './med-summary-file-status'

type MedSummaryFileCardProps =
  // EXTRACTED
  | {
      fileName: string
      status: MedSummaryFileStatus.EXTRACTED
      numEntries: number // Required for displaying entries extracted count
      errorMessage?: never
      onRemove?: never
      onClick?: () => void
    }
  // All other statuses
  | {
      fileName: string
      status: Exclude<MedSummaryFileStatus, MedSummaryFileStatus.EXTRACTED>
      numEntries?: never
      errorMessage?: string
      onRemove: () => void
      onClick?: () => void
    }

const entriesText = (count: number): string => {
  if (count === 0) return 'No entries extracted'
  if (count === 1) return '1 entry extracted'
  return `${count} entries extracted`
}

// Only these statuses represent a file the viewer can open, and only PDFs are viewable.
const kClickableStatuses: MedSummaryFileStatus[] = [MedSummaryFileStatus.COMPLETED, MedSummaryFileStatus.EXTRACTED]

/** An extracted file that produced no entries gets the warning treatment rather than the success one. */
function uploadCardStatus(status: MedSummaryFileStatus, numEntries?: number): UploadCardStatus {
  switch (status) {
    case MedSummaryFileStatus.UPLOADING:
      return UploadCardStatus.Uploading
    case MedSummaryFileStatus.PROCESSING:
      return UploadCardStatus.Processing
    case MedSummaryFileStatus.COMPLETED:
      return UploadCardStatus.Complete
    case MedSummaryFileStatus.EXTRACTED:
      return numEntries === 0 ? UploadCardStatus.Warning : UploadCardStatus.Complete
    case MedSummaryFileStatus.ERROR:
      return UploadCardStatus.Error
  }
}

/**
 * A pending-upload row on the billing summary Add Files surface. Thin wrapper over the shared
 * UploadCard that supplies the billing entries-extracted copy and open rules.
 */
export default function MedSummaryFileCard(props: MedSummaryFileCardProps) {
  const { fileName, status, errorMessage, numEntries, onClick } = props
  const isClickable = Boolean(onClick) && kClickableStatuses.includes(status) && fileName.toLowerCase().endsWith('.pdf')
  const isUploading = status === MedSummaryFileStatus.UPLOADING

  return (
    <UploadCard
      status={uploadCardStatus(status, numEntries)}
      filename={fileName}
      message={
        status === MedSummaryFileStatus.EXTRACTED
          ? entriesText(numEntries)
          : status === MedSummaryFileStatus.ERROR
          ? errorMessage || 'Document could not be read'
          : undefined
      }
      // The remove action cancels the transfer while it is still running, and dismisses the row after.
      onCancel={isUploading ? props.onRemove : undefined}
      dismissLabel={`Remove ${fileName}`}
      onDismiss={!isUploading ? props.onRemove : undefined}
      onRowClick={isClickable ? onClick : undefined}
    />
  )
}
